import React, { useMemo } from 'react';
import '@usb-shield/react-link/dist/library/styles/index.css';
import '@usb-shield/react-grid/dist/library/styles/index.css';
import 'highlight.js/styles/default.css';
import styles from "@/modules/products/ProductDocumentPage/DisplayViews/displayViews.module.scss";
import dynamic from "next/dynamic";
import BlockArrayWrapper from '@/components/BlockArrayWrapper/BlockArrayWrapper';
import GenericSection from '@/components/GenericSection/GenericSection';
import { ProductSectionsProps, TocItem } from './productSections.model';
import ProductSectionsDownload from './ProductSectionsDownload'

const ReactMarkDown = dynamic(
  () => import('@/components/ReactMarkDown/ReactMarkDown'),
  {
    ssr: false,
  }
)

const ProductSections = ({ pageData, onExtractToc }: ProductSectionsProps): JSX.Element => {
    const sections = pageData?.documentationSectionsCFM?.documentationSections;
    let pageContent: JSX.Element[] = [];

    // Extract TOC using useMemo for performance optimization
    const toc: TocItem[] = useMemo(() => {
        if (!Array.isArray(sections)) return [];
        return sections.map((section) => ({
            heading: section.heading || '',
            headingLevel: section.headingLevel || 'h2',
            id: section.heading?.toLowerCase().replace(/\s+/g, '-') || '',
        }));
    }, [sections]);

    // Notify parent component about the extracted TOC
    React.useEffect(() => {
        if (onExtractToc) {
            onExtractToc(toc);
        }
    }, [toc, onExtractToc]);

    if (Array.isArray(sections)) {
        for (const section of sections) {
            const CustomTag = section.headingLevel ? `${section.headingLevel.toLowerCase()}` : 'h2';
            let body: JSX.Element | null = null;

            switch (section.bodyFormat) {
                case 'markdown':
                    body = <ReactMarkDown content={section.markdown} />;
                    break;

                case 'download':
                    body = <ProductSectionsDownload ProductSectionsContent={{
                        ...section,
                        headingLevel: section.headingLevel as keyof JSX.IntrinsicElements | undefined
                    }} />
            
                    break

                case 'solutionsBlockWrapper':
                    body = (
                        <BlockArrayWrapper
                            title={section.solutionsBlockWrapper?.title}
                            shortDescription={section.solutionsBlockWrapper?.shortDescription}
                            textBlockWithImageParts={section.solutionsBlockWrapper?.textBlockWithImageParts || []}
                            gridOption="TenCol"
                            levelDownHeads={section.solutionsBlockWrapper?.gridOptions === 'true'}
                            ctaLink={section.solutionsBlockWrapper?.ctaLink}
                            ctaText={section.solutionsBlockWrapper?.ctaText}
                            linkAsButton={section.solutionsBlockWrapper?.linkAsButton}
                        />
                    );
                    break;

                case 'genericSection':
                    body = (
                        <GenericSection
                            heading={section.genericSection?.heading || ''}
                            bodyFormat={section.genericSection?.bodyFormat === 'html' ? 'html' : undefined}
                            body={section.genericSection?.body || ''}
                            displayTOC={section.genericSection?.displayTOC === 'true'}
                            gridOption="TenCol"
                        />
                    );
                    break;

                default:
                    body = null;
            }

            const renderedSection = (
                <div key={section.heading} id={section.heading?.toLowerCase().replace(/\s+/g, '-')}>
                    {section.heading && React.createElement(CustomTag as keyof JSX.IntrinsicElements, null, section.heading)}
                    {body && <div className={styles.documentPageMDContent}>{body}</div>}
                </div>
            );

            pageContent.push(renderedSection);
        }
    }

    return <>{pageContent}</>;
};

export default ProductSections;
